import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import { LOGOS, BRAND, SOCIAL_LINKS } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";

// TikTok icon (lucide-react doesn't ship one - inline SVG)
function TikTokIcon({ size = 16 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.71a8.16 8.16 0 0 0 4.77 1.52V7.79a4.85 4.85 0 0 1-1.84-1.1Z" />
        </svg>
    );
}

export default function Footer() {
    const { t } = useTranslation();
    return (
        <footer
            data-testid="site-footer"
            className="bg-ink text-cream/90 mt-24"
        >
            <div className="container-x py-20 grid gap-14 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Link to="/" className="inline-block" aria-label={`${BRAND.name} home`}>
                        <img
                            src={LOGOS.wordmarkCream}
                            alt={BRAND.name}
                            className="h-12 w-auto select-none"
                            draggable="false"
                        />
                    </Link>
                    <p className="mt-5 max-w-md text-sm leading-relaxed text-cream/70">
                        {t("footer.tagline")}
                    </p>
                </div>

                <div className="lg:col-span-3">
                    <h4 className="font-heading text-base text-cream mb-5">{t("footer.exploreHeading")}</h4>
                    <ul className="space-y-3 text-sm">
                        <li><Link data-testid="footer-link-home" to="/" className="text-cream/70 hover:text-clay transition-colors">{t("footer.links.home")}</Link></li>
                        <li><Link data-testid="footer-link-about" to="/about" className="text-cream/70 hover:text-clay transition-colors">{t("footer.links.about")}</Link></li>
                        <li><Link data-testid="footer-link-homeowners" to="/for-homeowners" className="text-cream/70 hover:text-clay transition-colors">{t("footer.links.homeowners")}</Link></li>
                        <li><Link data-testid="footer-link-providers" to="/for-service-providers" className="text-cream/70 hover:text-clay transition-colors">{t("footer.links.providers")}</Link></li>
                        <li><Link data-testid="footer-link-contact" to="/contact" className="text-cream/70 hover:text-clay transition-colors">{t("footer.links.contact")}</Link></li>
                    </ul>
                </div>

                <div className="lg:col-span-4">
                    <h4 className="font-heading text-base text-cream mb-5">{t("footer.followHeading")}</h4>
                    <p className="text-sm text-cream/70 leading-relaxed">
                        {t("footer.followBody")}
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <a
                            href={SOCIAL_LINKS.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                            data-testid="social-instagram"
                            className="w-11 h-11 rounded-full border border-cream/20 flex items-center justify-center hover:bg-clay hover:border-clay hover:text-ink transition-colors"
                        >
                            <Instagram size={18} />
                        </a>
                        <a
                            href={SOCIAL_LINKS.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook"
                            data-testid="social-facebook"
                            className="w-11 h-11 rounded-full border border-cream/20 flex items-center justify-center hover:bg-clay hover:border-clay hover:text-ink transition-colors"
                        >
                            <Facebook size={18} />
                        </a>
                        <a
                            href={SOCIAL_LINKS.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TikTok"
                            data-testid="social-tiktok"
                            className="w-11 h-11 rounded-full border border-cream/20 flex items-center justify-center hover:bg-clay hover:border-clay hover:text-ink transition-colors"
                        >
                            <TikTokIcon size={18} />
                        </a>
                        <a
                            href={SOCIAL_LINKS.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="LinkedIn"
                            data-testid="social-linkedin"
                            className="w-11 h-11 rounded-full border border-cream/20 flex items-center justify-center hover:bg-clay hover:border-clay hover:text-ink transition-colors"
                        >
                            <Linkedin size={18} />
                        </a>
                    </div>
                </div>
            </div>

            <div className="border-t border-cream/10">
                <div className="container-x py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-cream/50 font-email">
                    <p>© {new Date().getFullYear()} {BRAND.name}. {t("footer.rightsReserved")}</p>
                    <p>{BRAND.tagline}</p>
                </div>
            </div>
        </footer>
    );
}
